import { useEffect, useState } from 'react'
import { createFileRoute, Outlet, useNavigate, Link, useLocation } from '@tanstack/react-router'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { accountQueries } from '@/services/accountApi'
import { adminQueries } from '@/services/adminApi'
import { useRequireAuth } from '@/hooks/useRequireAuth'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  LayoutDashboard,
  Users,
  Phone,
  MessageSquare,
  MoreHorizontal,
  CreditCard,
  Tag,
  Hash,
  Network,
  CalendarClock,
  LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
})

type NavTo = '/admin' | '/admin/users' | '/admin/calls' | '/admin/transactions' | '/admin/pricing' | '/admin/phone-numbers' | '/admin/affiliates' | '/admin/support' | '/admin/scheduler'

const allNavItems: Array<{ to: NavTo; label: string; exact: boolean; icon: React.ElementType; showBadge?: boolean }> = [
  { to: '/admin',               label: 'Dashboard',    exact: true,  icon: LayoutDashboard },
  { to: '/admin/users',         label: 'Users',        exact: false, icon: Users },
  { to: '/admin/calls',         label: 'Calls',        exact: false, icon: Phone },
  { to: '/admin/transactions',  label: 'Transactions', exact: false, icon: CreditCard },
  { to: '/admin/pricing',       label: 'Pricing',      exact: false, icon: Tag },
  { to: '/admin/phone-numbers', label: 'Phone Numbers',exact: false, icon: Hash },
  { to: '/admin/affiliates',    label: 'Referrals',    exact: false, icon: Network },
  { to: '/admin/support',       label: 'Support',      exact: false, icon: MessageSquare, showBadge: true },
  { to: '/admin/scheduler',     label: 'Scheduler',    exact: false, icon: CalendarClock },
]

// Items pinned to the bottom bar — chosen for frequency of use
const BOTTOM_BAR_KEYS: NavTo[] = ['/admin', '/admin/users', '/admin/calls', '/admin/support']
const bottomBarItems = allNavItems.filter(i => BOTTOM_BAR_KEYS.includes(i.to))
const overflowItems   = allNavItems.filter(i => !BOTTOM_BAR_KEYS.includes(i.to))

// ── Desktop sidebar nav ───────────────────────────────────────────────────────

function SidebarNav({
  unreadCount,
  logout,
  onNavClick,
}: {
  unreadCount: number
  logout: () => void
  onNavClick?: () => void
}) {
  return (
    <>
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {allNavItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            activeOptions={{ exact: item.exact }}
            activeProps={{ className: 'bg-primary text-primary-foreground' }}
            inactiveProps={{ className: 'text-muted-foreground hover:bg-muted hover:text-foreground' }}
            className="flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors"
            onClick={onNavClick}
          >
            <span className="flex items-center gap-2">
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </span>
            {item.showBadge && unreadCount > 0 && (
              <span className="bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>
        ))}
      </nav>
      <div className="p-4 border-t">
        <Button variant="outline" size="sm" className="w-full" onClick={logout}>
          Logout
        </Button>
      </div>
    </>
  )
}

// ── Mobile bottom nav bar ─────────────────────────────────────────────────────

function MobileBottomBar({
  unreadCount,
  onMoreClick,
  overflowActive,
}: {
  unreadCount: number
  onMoreClick: () => void
  overflowActive: boolean
}) {
  const location = useLocation()

  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 flex items-stretch border-t bg-background/95 backdrop-blur safe-bottom">
      {bottomBarItems.map((item) => {
        const isActive = item.exact
          ? location.pathname === item.to
          : location.pathname.startsWith(item.to)
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            <div className="relative">
              <item.icon className="h-5 w-5" />
              {item.showBadge && unreadCount > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            {item.label}
          </Link>
        )
      })}

      {/* More button */}
      <button
        onClick={onMoreClick}
        className={cn(
          'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] font-medium transition-colors',
          overflowActive ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        <MoreHorizontal className="h-5 w-5" />
        More
      </button>
    </nav>
  )
}

// ── Mobile overflow drawer ────────────────────────────────────────────────────

function MobileOverflowDrawer({
  open,
  onOpenChange,
  unreadCount,
  logout,
  onNavClick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  unreadCount: number
  logout: () => void
  onNavClick: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:hidden fixed bottom-0 top-auto translate-y-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom rounded-t-2xl rounded-b-none border-t border-border border-x-0 border-b-0 max-h-[85vh] p-0 flex flex-col">
        <div className="px-6 pt-5 pb-2">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">More</DialogTitle>
          </DialogHeader>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-2 space-y-1">
          {overflowItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.exact }}
              activeProps={{ className: 'bg-primary text-primary-foreground' }}
              inactiveProps={{ className: 'text-muted-foreground hover:bg-muted hover:text-foreground' }}
              className="flex items-center justify-between px-3 py-3 rounded-md text-sm font-medium transition-colors"
              onClick={onNavClick}
            >
              <span className="flex items-center gap-3">
                <item.icon className="h-4 w-4 shrink-0" />
                {item.label}
              </span>
              {item.showBadge && unreadCount > 0 && (
                <span className="bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="px-3 pb-6 pt-2 border-t">
          <button
            onClick={() => { logout(); onNavClick() }}
            className="flex w-full items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            Logout
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Layout ────────────────────────────────────────────────────────────────────

export default function AdminLayout() {
  const { isAuthenticated, logout } = useAuth()
  const { data: accountData, isLoading } = useQuery(accountQueries.me())
  const { data: unreadData } = useQuery(adminQueries.inboxUnreadCount())
  const navigate = useNavigate()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  useRequireAuth()

  const isAdmin = accountData?.account?.role === 'admin'
  const unreadCount = unreadData?.unreadCount ?? 0

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate({ to: '/login', replace: true })
    }
  }, [isLoading, isAdmin, navigate])

  if (!isAuthenticated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  if (!isAdmin) return null

  // Is the current route one of the overflow items?
  const overflowActive = overflowItems.some(i =>
    i.exact ? location.pathname === i.to : location.pathname.startsWith(i.to)
  )

  return (
    <div className="min-h-screen overflow-x-hidden md:grid md:grid-cols-[14rem_1fr] md:h-screen">

      {/* Desktop sidebar — hidden on mobile */}
      <aside className="hidden md:flex sticky top-0 h-screen flex-col bg-muted/50 border-r">
        <div className="p-4 border-b">
          <h1 className="text-lg font-bold">PhoneSalad Admin</h1>
        </div>
        <SidebarNav unreadCount={unreadCount} logout={logout} />
      </aside>

      {/* Main content — extra bottom padding on mobile to clear the nav bar */}
      <main className="min-w-0 overflow-y-auto overflow-x-auto p-6 pb-[calc(1.5rem+4rem)] md:pb-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav bar */}
      <MobileBottomBar
        unreadCount={unreadCount}
        onMoreClick={() => setDrawerOpen(true)}
        overflowActive={overflowActive || drawerOpen}
      />

      {/* Mobile overflow drawer */}
      <MobileOverflowDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        unreadCount={unreadCount}
        logout={logout}
        onNavClick={() => setDrawerOpen(false)}
      />
    </div>
  )
}
